import {
  GenericFilter,
  ProjectAggregate,
  TaxonomyAggregateResp,
  TaxonomyCategoryResp,
  TimeRangeConfig,
  TimeRangeKind,
} from '@/api/generated/model';
import { formatToLocaleDateValue, getTimezoneName, subtractDays } from '@/utils/date.util';
import { getDateTRangeFromKindWithoutCustom } from '@/utils/get-date-range-from-kind';
import { getListTaxonomyQueryKey } from '@/api/generated/taxonomy/taxonomy';
import { Duration } from '@/lib/duration';
import {
  QUERY_KEY_CONFIGURATION,
  QUERY_KEY_FETCH_CUSTOM_AGGREGATE_CATEGORIES,
  QUERY_KEY_INTERACTION_AGGREGATES,
  QUERY_KEY_USER_INTENTS_PAGE__ACTIONS,
  QUERY_KEY_USER_INTENTS_PAGE__INTENTS,
  QUERY_KEY_USER_INTENTS_PAGE__TOTALS,
  QUERY_KEY_USER_QUERIES,
  QUERY_KEY_USER_TOPIC__TOTALS,
} from '@/constants/query-keys';
import { filterToOperator, isContainsFilter } from '@/comps/FilterPicker/filter-operators';
import { formatRuleExpressionPreview } from '@/comps/rule-builder/expression-preview';
import {
  DEFAULT_RULE_MAX_CONDITIONS_PER_LEVEL,
  DEFAULT_RULE_MAX_GROUP_NESTING_DEPTH,
  canGroupSelectedChildren as sharedCanGroupSelectedChildren,
  findRuleGroupById,
  findRuleGroupDepth,
  updateRuleGroupInTree,
  wrapContiguousChildren as sharedWrapContiguousChildren,
} from '@/comps/rule-builder/tree';
import { RuleGroupNode, RuleGroupOp, RuleNode } from '@/comps/rule-builder/types';
import { FILTERS, isFilterDefined, removeTagPrefix, TAG_PREFIX } from '@/models/common/filters';
import { pipe } from 'effect';
import snakeCase from 'lodash/snakeCase';
import { QueryClient } from '@tanstack/react-query';
import { capFirst } from '@/lib/capfirst';
import { CohortModel } from '@/models/cohort/internal-models';
import {
  buildTaxonomyCycleContextForDraft,
  COHORT_TAXONOMY_CYCLE_COHORT_ERROR,
  getCyclingCohortIdsInTaxonomyDraft,
  isTaxonomyCycleCheckAggregate,
} from '@/models/cohort/cohort-taxonomy-cycle';

const CATEGORY_API_KIND_BY_AGGREGATE: Record<string, string> = {
  topic: 'topic',
  user_intent: 'intent',
  user_action: 'action',
  category: 'sub_topic',
  model_issue: 'model_suggestion',
  model_suggestion: 'model_suggestion',
  type_of_problem_v2: 'model_suggestion',
  moderation_classes: 'moderation_classes',
  business_classes: 'business_classes',
};

export function toCategoryApiKind(aggregateName: string): string {
  return CATEGORY_API_KIND_BY_AGGREGATE[aggregateName] ?? aggregateName;
}

export function getCategoryUpdateKind(category: Pick<TaxonomyCategoryResp, 'category_name'>): string {
  return toCategoryApiKind(category.category_name);
}

export const clean = (name: string) => pipe(name, (s) => s.replace(/_/g, ' '), capFirst);

export function getTaxonomyAggregateConfig(row: TaxonomyAggregateResp): ProjectAggregate {
  return row.aggregate;
}

export function invalidateAfterCategoryChange(client: QueryClient) {
  client.invalidateQueries({ queryKey: [QUERY_KEY_FETCH_CUSTOM_AGGREGATE_CATEGORIES] });
  client.invalidateQueries({ queryKey: [QUERY_KEY_INTERACTION_AGGREGATES] });
  client.invalidateQueries({ queryKey: [QUERY_KEY_USER_INTENTS_PAGE__INTENTS] });
  client.refetchQueries({ queryKey: [QUERY_KEY_USER_INTENTS_PAGE__ACTIONS] });
  client.invalidateQueries({ queryKey: [QUERY_KEY_USER_QUERIES] });
  client.invalidateQueries({ queryKey: [QUERY_KEY_USER_INTENTS_PAGE__TOTALS] });
  client.refetchQueries({ queryKey: [QUERY_KEY_USER_TOPIC__TOTALS] });
}

export async function invalidateAfterMappingChange(client: QueryClient, projectId: string) {
  invalidateAfterCategoryChange(client);
  await client.invalidateQueries({ queryKey: getListTaxonomyQueryKey(projectId) });
}

export function invalidateProjectConfiguration(client: QueryClient, projectId: string) {
  return client.invalidateQueries({ queryKey: [QUERY_KEY_CONFIGURATION, projectId], refetchType: 'all' });
}

type ClassificationRuleCriterionPayload = Record<string, unknown>;
type ClassificationRuleFilter = GenericFilter & { is_exclude?: boolean };

const singleOrAnyOf = (
  criteria: ClassificationRuleCriterionPayload[],
): ClassificationRuleCriterionPayload | undefined => {
  if (criteria.length === 0) return undefined;
  if (criteria.length === 1) return criteria[0];

  return {
    type: 'any_of',
    criteria,
  };
};

const singleOrAllOf = (
  criteria: ClassificationRuleCriterionPayload[],
): ClassificationRuleCriterionPayload | undefined => {
  if (criteria.length === 0) return undefined;
  if (criteria.length === 1) return criteria[0];

  return {
    type: 'all_of',
    criteria,
  };
};

const listContainsCriterion = (field: string, values: string[]): ClassificationRuleCriterionPayload => ({
  type: 'list_contains',
  field,
  values,
});

const tagMatchCriterionFromFilter = (filter: GenericFilter): ClassificationRuleCriterionPayload | undefined => {
  const tagKey = removeTagPrefix(filter.column);

  return singleOrAnyOf(
    filter.values.map((value) => ({
      type: 'tag_match',
      key: tagKey,
      value,
    })),
  );
};

const numericComparisonCriterionFromValues = (values: string[]): ClassificationRuleCriterionPayload | undefined => {
  const criteria = values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .map((value) => ({
      type: 'numeric_comparison',
      field: 'feedback_value',
      value,
      operator: '=',
    }));

  return singleOrAnyOf(criteria);
};

const criterionFromFilter = (filter: GenericFilter): ClassificationRuleCriterionPayload | undefined => {
  let values = filter.values.filter(Boolean);
  if (values.length === 0) {
    const search = filter.search?.trim();
    if (!search) return undefined;
    values = [search];
  }

  let criterion: ClassificationRuleCriterionPayload | undefined;

  if (filter.column === FILTERS.kindEmotions) {
    criterion = listContainsCriterion('emotions', values);
  } else if (filter.column === FILTERS.kindDataSource) {
    criterion = listContainsCriterion('rag_sources', values);
  } else if (filter.column === FILTERS.kindUserRating) {
    criterion = numericComparisonCriterionFromValues(values);
  } else if (filter.column.startsWith(TAG_PREFIX)) {
    criterion = tagMatchCriterionFromFilter({ ...filter, values });
  } else if (filter.column === FILTERS.columnCohort) {
    criterion = {
      type: 'cohort_match',
      ids: values,
      mode: 'any',
    };
  }

  if (!criterion) return undefined;

  const isExcluded = filter.is_excluded ?? (filter as GenericFilter & { is_exclude?: boolean }).is_exclude;

  if (isExcluded) {
    return {
      type: 'not',
      criterion,
    };
  }

  return criterion;
};

export function classificationRuleFromFilters(filters: GenericFilter[]): Record<string, unknown> {
  const criterion = classificationRuleCriterionFromFilters(filters);
  if (!criterion) {
    throw new Error('A classification rule requires at least one supported filter.');
  }

  return criterion;
}

export function classificationRuleCriterionFromFilters(
  filters: GenericFilter[],
): ClassificationRuleCriterionPayload | undefined {
  const criteria = filters
    .map(criterionFromFilter)
    .filter((criterion): criterion is ClassificationRuleCriterionPayload => criterion !== undefined);

  return singleOrAllOf(criteria);
}

export const isClassificationRuleCriterionPayload = (value: unknown): value is ClassificationRuleCriterionPayload =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const stringValuesFromRuleValues = (values: unknown): string[] | undefined => {
  if (!Array.isArray(values)) return undefined;

  const result = values.filter((value): value is string => typeof value === 'string' && value !== '');
  return result.length > 0 ? result : undefined;
};

const filterFromRuleParts = (column: string, values: string[], isExcluded: boolean): ClassificationRuleFilter => ({
  column,
  values,
  is_excluded: isExcluded,
  is_exclude: isExcluded,
});

const filtersFromRuleCriterion = (
  rule: ClassificationRuleCriterionPayload,
  isExcluded = false,
): ClassificationRuleFilter[] | undefined => {
  if (rule.type === 'not') {
    if (!isClassificationRuleCriterionPayload(rule.criterion)) return undefined;

    return filtersFromRuleCriterion(rule.criterion, !isExcluded);
  }

  if (rule.type === 'all_of') {
    if (!Array.isArray(rule.criteria)) return undefined;

    const childFilters = rule.criteria.map((criterion) => {
      if (!isClassificationRuleCriterionPayload(criterion)) return undefined;
      return filtersFromRuleCriterion(criterion, isExcluded);
    });

    if (childFilters.some((filter) => filter === undefined)) return undefined;

    return childFilters.flatMap((filter) => filter ?? []);
  }

  if (rule.type ==='any_of') {
    if (!Array.isArray(rule.criteria)) return undefined;

    const parsedChildFilters = rule.criteria.map((criterion) => {
      if (!isClassificationRuleCriterionPayload(criterion)) return undefined;
      return filtersFromRuleCriterion(criterion, isExcluded);
    });

    if (parsedChildFilters.some((filter) => filter === undefined)) return undefined;

    const childFilters = parsedChildFilters.flatMap((filter) => filter ?? []);

    if (childFilters.length === 0) return undefined;

    const [firstFilter] = childFilters;
    const canMerge = childFilters.every(
      (filter) => filter.column === firstFilter.column && filter.is_excluded === firstFilter.is_excluded,
    );
    if (!canMerge) return undefined;

    return [
      filterFromRuleParts(
        firstFilter.column,
        globalThis.Array.from(new Set(childFilters.flatMap((filter) => filter.values))),
        firstFilter.is_excluded,
      ),
    ];
  }

  if (rule.type ==='list_contains') {
    const values = stringValuesFromRuleValues(rule.values);
    if (!values) return undefined;

    if (rule.field === 'emotions') {
      return [filterFromRuleParts(FILTERS.kindEmotions, values, isExcluded)];
    }

    if (rule.field === 'rag_sources') {
      return [filterFromRuleParts(FILTERS.kindDataSource, values, isExcluded)];
    }
  }

  if (rule.type === 'numeric_comparison' && rule.field === 'feedback_value' && rule.operator === '=') {
    const value = typeof rule.value === 'number' || typeof rule.value === 'string' ? String(rule.value) : undefined;
    if (!value) return undefined;

    return [filterFromRuleParts(FILTERS.kindUserRating, [value], isExcluded)];
  }

  if (rule.type === 'tag_match' && typeof rule.key === 'string' && typeof rule.value === 'string') {
    return [filterFromRuleParts(`${TAG_PREFIX}${rule.key}`, [rule.value], isExcluded)];
  }

  if (rule.type === 'cohort_match') {
    const ids = stringValuesFromRuleValues(rule.ids);
    if (!ids) return undefined;

    return [filterFromRuleParts(FILTERS.columnCohort, ids, isExcluded)];
  }

  return undefined;
};

export function filtersFromClassificationRule(rule: Record<string, unknown>): GenericFilter[] {
  return normalizeTaxonomyFilters(
    filtersFromRuleCriterion(rule)?.filter((filter) => filter.values.length > 0 || !!filter.search?.trim()) ?? [],
  );
}

/** Coerce legacy contains / is_not filter shapes to equals for taxonomy pickers. */
export function normalizeTaxonomyFilters(filters: GenericFilter[]): GenericFilter[] {
  return filters.map((filter) => {
    const search = filter.search?.trim();
    const values = search && (filter.values?.length ?? 0) === 0 ? [search] : filter.values.filter(Boolean);

    return {
      ...filter,
      values,
      search: undefined,
      is_exclude: false,
      is_excluded: false,
    };
  });
}

export function getInvalidFilterColumns(filters: GenericFilter[]): string[] {
  return filters.filter((filter) => !isFilterDefined(filter)).map((filter) => filter.column);
}

export function areTaxonomyFiltersValid(filters: GenericFilter[]): boolean {
  return filters.length > 0 && filters.every(isFilterDefined);
}

export type CohortIdAndName = { id: string; name: string };

const isCohortFilter = (filter: GenericFilter): boolean => filter.column === FILTERS.columnCohort;

export function mapCohortFilterNamesToIds(filters: GenericFilter[], cohorts: CohortIdAndName[]): GenericFilter[] {
  const nameToId = new Map(cohorts.map((cohort) => [cohort.name, cohort.id]));

  return filters.map((filter) => {
    if (!isCohortFilter(filter)) return filter;

    return {
      ...filter,
      values: filter.values.map((name) => nameToId.get(name) ?? name),
    };
  });
}

export function mapCohortFilterIdsToNames(filters: GenericFilter[], cohorts: CohortIdAndName[]): GenericFilter[] {
  const idToName = new Map(cohorts.map((cohort) => [cohort.id, cohort.name]));

  return filters.map((filter) => {
    if (!isCohortFilter(filter)) return filter;

    return {
      ...filter,
      values: filter.values.map((id) => idToName.get(id) ?? id),
    };
  });
}

export function getUnresolvedCohortFilterNames(filters: GenericFilter[], cohorts: CohortIdAndName[]): string[] {
  const nameToId = new Map(cohorts.map((cohort) => [cohort.name, cohort.id]));
  const unresolved: string[] = [];

  for (const filter of filters) {
    if (!isCohortFilter(filter)) continue;

    for (const name of filter.values) {
      if (!nameToId.has(name)) {
        unresolved.push(name);
      }
    }
  }

  return unresolved;
}

export function mapCohortFilterNamesToIdsInCustomizedTree(
  tree: CustomizedRuleGroupNode,
  cohorts: CohortIdAndName[],
): CustomizedRuleGroupNode {
  const mapNode = (node: CustomizedRuleNode): CustomizedRuleNode => {
    if (node.kind ==='filter') {
      return { ...node, filters: mapCohortFilterNamesToIds(node.filters, cohorts) };
    }

    if (node.kind === 'group') {
      return { ...node, children: node.children.map(mapNode) };
    }

    return node;
  };

  return mapNode(tree) as CustomizedRuleGroupNode;
}

export function mapCohortFilterIdsToNamesInCustomizedTree(
  tree: CustomizedRuleGroupNode,
  cohorts: CohortIdAndName[],
): CustomizedRuleGroupNode {
  const mapNode = (node: CustomizedRuleNode): CustomizedRuleNode => {
    if (node.kind === 'filter') {
      return { ...node, filters: mapCohortFilterIdsToNames(node.filters, cohorts) };
    }

    if (node.kind === 'group') {
      return { ...node, children: node.children.map(mapNode) };
    }

    return node;
  };

  return mapNode(tree) as CustomizedRuleGroupNode;
}

export function getUnresolvedCohortFilterNamesFromTree(
  tree: CustomizedRuleGroupNode,
  cohorts: CohortIdAndName[],
): string[] {
  const unresolved: string[] = [];

  const visit = (node: CustomizedRuleNode) => {
    if (node.kind === 'filter') {
      unresolved.push(...getUnresolvedCohortFilterNames(node.filters, cohorts));
      return;
    }

    if (node.kind === 'group') {
      for (const child of node.children) {
        visit(child);
      }
    }
  };

  visit(tree);
  return unresolved;
}

export type CohortFilterSubmitBlock = { kind: 'loading' } | { kind: 'unresolved'; names: string[] } | { kind: 'cycle' };

export type TaxonomyCycleCheckInput = {
  cohortModels: CohortModel[];
  taxonomyItems?: TaxonomyAggregateResp[];
  categoryName: string;
  value: string;
  classificationRule: unknown;
  excludeValue?: string;
};

export function getTaxonomyCycleSubmitBlock(input: TaxonomyCycleCheckInput): { kind: 'cycle' } | null {
  if (!isTaxonomyCycleCheckAggregate(input.categoryName)) return null;

  const context = buildTaxonomyCycleContextForDraft({
    cohorts: input.cohortModels,
    taxonomyItems: input.taxonomyItems,
    categoryName: input.categoryName,
    value: input.value,
    classificationRule: input.classificationRule,
    excludeValue: input.excludeValue,
  });

  const cycling = getCyclingCohortIdsInTaxonomyDraft(context, input.classificationRule, {
    categoryName: input.categoryName,
    value: input.value,
  });

  return cycling.length > 0 ? { kind: 'cycle' } : null;
}

export function resolveCohortFilterSubmitBlock(
  filters: GenericFilter[],
  cohorts: CohortIdAndName[],
  isLoadingCohorts: boolean,
  cycleInput?: TaxonomyCycleCheckInput,
): CohortFilterSubmitBlock | null {
  const base = getCohortFilterSubmitBlock(filters, cohorts, isLoadingCohorts);
  if (base) return base;
  if (!cycleInput) return null;
  return getTaxonomyCycleSubmitBlock(cycleInput);
}

export function resolveCohortFilterSubmitBlockFromTree(
  tree: CustomizedRuleGroupNode,
  cohorts: CohortIdAndName[],
  isLoadingCohorts: boolean,
  cycleInput?: TaxonomyCycleCheckInput,
): CohortFilterSubmitBlock | null {
  const base = getCohortFilterSubmitBlockFromTree(tree, cohorts, isLoadingCohorts);
  if (base) return base;
  if (!cycleInput) return null;
  return getTaxonomyCycleSubmitBlock(cycleInput);
}

export function buildTaxonomyCycleCheckInputForFilters(
  filters: GenericFilter[],
  cohortModels: CohortModel[],
  taxonomyItems: TaxonomyAggregateResp[] | undefined,
  categoryName: string,
  value: string,
  excludeValue?: string,
): TaxonomyCycleCheckInput {
  return {
    cohortModels,
    taxonomyItems,
    categoryName,
    value,
    excludeValue,
    classificationRule: classificationRuleFromFilters(mapCohortFilterNamesToIds(filters, cohortModels)),
  };
}

export function buildTaxonomyCycleCheckInputForTree(
  tree: CustomizedRuleGroupNode,
  cohortModels: CohortModel[],
  taxonomyItems: TaxonomyAggregateResp[] | undefined,
  categoryName: string,
  value: string,
  excludeValue?: string,
): TaxonomyCycleCheckInput | undefined {
  const classificationRule = tryClassificationRuleFromCustomizedTree(
    mapCohortFilterNamesToIdsInCustomizedTree(tree, cohortModels),
  );
  if (!classificationRule) {
    return undefined;
  }

  return {
    cohortModels,
    taxonomyItems,
    categoryName,
    value,
    excludeValue,
    classificationRule,
  };
}

const treeHasCohortFilters = (tree: CustomizedRuleGroupNode): boolean => {
  const visit = (node: CustomizedRuleNode): boolean => {
    if (node.kind === 'filter') {
      return node.filters.some(isCohortFilter);
    }

    if (node.kind === 'group') {
      return node.children.some(visit);
    }

    return false;
  };

  return visit(tree);
};

export function getCohortFilterSubmitBlock(
  filters: GenericFilter[],
  cohorts: CohortIdAndName[],
  isLoadingCohorts: boolean,
): CohortFilterSubmitBlock | null {
  if (!filters.some(isCohortFilter)) {
    return null;
  }

  if (isLoadingCohorts) {
    return { kind: 'loading' };
  }

  const unresolved = getUnresolvedCohortFilterNames(filters, cohorts);
  if (unresolved.length > 0) {
    return { kind: 'unresolved', names: unresolved };
  }

  return null;
}

export function getCohortFilterSubmitBlockFromTree(
  tree: CustomizedRuleGroupNode,
  cohorts: CohortIdAndName[],
  isLoadingCohorts: boolean,
): CohortFilterSubmitBlock | null {
  if (!treeHasCohortFilters(tree)) {
    return null;
  }

  if (isLoadingCohorts) {
    return { kind: 'loading' };
  }

  const unresolved = getUnresolvedCohortFilterNamesFromTree(tree, cohorts);
  if (unresolved.length > 0) {
    return { kind: 'unresolved', names: unresolved };
  }

  return null;
}

export function cohortFilterSubmitBlockMessage(block: CohortFilterSubmitBlock): string {
  if (block.kind === 'loading') {
    return 'Cohorts are still loading. Please wait and try again.';
  }

  if (block.kind === 'cycle') {
    return COHORT_TAXONOMY_CYCLE_COHORT_ERROR;
  }

  if (block.names.length === 1) {
    return `Unknown cohort: ${block.names[0]}`;
  }

  return `Unknown cohorts: ${block.names.join(', ')}`;
}

export function isCohortFiltersSubmitBlocked(
  filters: GenericFilter[],
  cohorts: CohortIdAndName[],
  isLoadingCohorts: boolean,
): boolean {
  return getCohortFilterSubmitBlock(filters, cohorts, isLoadingCohorts)?.kind === 'loading';
}

export function isCohortFiltersSubmitBlockedInTree(
  tree: CustomizedRuleGroupNode,
  cohorts: CohortIdAndName[],
  isLoadingCohorts: boolean,
): boolean {
  return getCohortFilterSubmitBlockFromTree(tree, cohorts, isLoadingCohorts)?.kind === 'loading';
}

export const TAXONOMY_CLASS_TOAST = {
  creating: 'Creating taxonomy class...',
  created: 'Taxonomy class created successfully',
  createFailed: 'Failed to create taxonomy class',
  updating: 'Updating taxonomy class...',
  updated: 'Taxonomy class updated successfully',
  updateFailed: 'Failed to update taxonomy class',
  renaming: 'Renaming taxonomy class...',
  renamed: 'Taxonomy class renamed successfully',
  renameFailed: 'Failed to rename taxonomy class',
  deleting: 'Deleting taxonomy class...',
  deleted: 'Taxonomy class deleted successfully',
  deleteFailed: 'Failed to delete taxonomy class',
  deletingBulk: 'Deleting taxonomy classes...',
  deletedBulk: 'Taxonomy classes deleted successfully',
  deleteFailedBulk: 'Failed to delete taxonomy classes',
} as const;

export const TAXONOMY_CLASS_DIALOG = {
  add: 'Add taxonomy class',
  update: 'Update taxonomy class',
  rename: 'Rename taxonomy class',
} as const;

export const CLASSIFIER_FIELD_OPTIONS = [
  {
    value: 'query',
    label: 'User query',
    tooltip: 'The message the end user sends to your application',
  },
  {
    value: 'assistant_query',
    label: 'Agent answer',
    tooltip: 'The response your application returns to the user',
  },
  {
    value: 'user_text',
    label: 'Input',
    tooltip: 'The full input passed to the model, including system and context',
  },
  {
    value: 'assistant_text',
    label: 'Output',
    tooltip: 'The raw model output before any post-processing',
  },
] as const;

export type ClassifierField = (typeof CLASSIFIER_FIELD_OPTIONS)[number]['value'];

type AdjacentClassifierFieldName =
  | 'previous_user_query'
  | 'next_user_query'
  | 'previous_assistant_query'
  | 'next_assistant_query'
  | 'previous_user_text'
  | 'next_user_text'
  | 'previous_assistant_text'
  | 'next_assistant_text';

const ADJACENT_CLASSIFIER_FIELD_BY_BASE = {
  query: { previous: 'previous_user_query', next: 'next_user_query' },
  assistant_query: { previous: 'previous_assistant_query', next: 'next_assistant_query' },
  user_text: { previous: 'previous_user_text', next: 'next_user_text' },
  assistant_text: { previous: 'previous_assistant_text', next: 'next_assistant_text' },
} as const satisfies Record<
  ClassifierField,
  { previous: AdjacentClassifierFieldName; next: AdjacentClassifierFieldName }
>;

export const ADJACENT_CLASSIFIER_FIELDS = [
  ADJACENT_CLASSIFIER_FIELD_BY_BASE.query.previous,
  ADJACENT_CLASSIFIER_FIELD_BY_BASE.query.next,
  ADJACENT_CLASSIFIER_FIELD_BY_BASE.assistant_query.previous,
  ADJACENT_CLASSIFIER_FIELD_BY_BASE.assistant_query.next,
  ADJACENT_CLASSIFIER_FIELD_BY_BASE.user_text.previous,
  ADJACENT_CLASSIFIER_FIELD_BY_BASE.user_text.next,
  ADJACENT_CLASSIFIER_FIELD_BY_BASE.assistant_text.previous,
  ADJACENT_CLASSIFIER_FIELD_BY_BASE.assistant_text.next,
] as const;

export type AdjacentClassifierField = (typeof ADJACENT_CLASSIFIER_FIELDS)[number];

export type ClassifierTargetField = ClassifierField | AdjacentClassifierField;

export const DEFAULT_CLASSIFIER_FIELD: ClassifierField = 'query';

const ADJACENT_CLASSIFIER_FIELD_VALUES = new Set<AdjacentClassifierField>(ADJACENT_CLASSIFIER_FIELDS);

export function isAdjacentClassifierField(field: ClassifierTargetField): field is AdjacentClassifierField {
  return ADJACENT_CLASSIFIER_FIELD_VALUES.has(field as AdjacentClassifierField);
}

export function getClassifierFieldOptionLabel(field: ClassifierTargetField): string {
  const baseOption = CLASSIFIER_FIELD_OPTIONS.find((option) => option.value === field);
  if (baseOption) return baseOption.label;

  for (const baseField of CLASSIFIER_FIELD_OPTIONS) {
    const adjacent = ADJACENT_CLASSIFIER_FIELD_BY_BASE[baseField.value];
    if (adjacent.previous === field) {
      return `Previous ${baseField.label.toLowerCase()}`;
    }
    if (adjacent.next === field) {
      return `Next ${baseField.label.toLowerCase()}`;
    }
  }

  return field;
}

export function getClassifierFieldOptionTooltip(field: ClassifierTargetField): string | undefined {
  if (isAdjacentClassifierField(field)) {
    for (const baseField of CLASSIFIER_FIELD_OPTIONS) {
      const adjacent = ADJACENT_CLASSIFIER_FIELD_BY_BASE[baseField.value];
      if (adjacent.previous === field || adjacent.next === field) {
        const position = adjacent.previous === field ? 'previous' : 'next';
        return `The ${position} turn's ${baseField.label.toLowerCase()}`;
      }
    }
    return undefined;
  }

  return CLASSIFIER_FIELD_OPTIONS.find((option) => option.value === field)?.tooltip;
}

export type RetroactiveMode = 'keywords' | 'all';

export function llmClassificationRule(field: ClassifierTargetField, description: string): Record<string, unknown> {
  return {
    type: 'llm_classification',
    fields: [field],
    description,
  };
}

export function getLlmClassificationFields(criterion: Record<string, unknown>): string[] {
  const { fields } = criterion;
  if (!Array.isArray(fields)) return [];

  return fields.filter((field): field is string => typeof field === 'string');
}

export function parseClassifierFieldFromLlmFields(fields: unknown): ClassifierTargetField {
  if (!Array.isArray(fields) || fields.length === 0) {
    return DEFAULT_CLASSIFIER_FIELD;
  }

  return parseClassifierFieldFromRule(fields[0]);
}

export function regexMatchRule(field: ClassifierTargetField, pattern: string): Record<string, unknown> {
  return {
    type: 'regex_match',
    field,
    pattern,
  };
}

export function isValidRegexPattern(pattern: string): boolean {
  if (!pattern.trim()) return false;
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
}

export type CustomizedRuleDescriptionNode = {
  id: string;
  kind: 'description';
  field: ClassifierTargetField;
  description: string;
  negated: boolean;
};

export type CustomizedRuleRegexNode = {
  id: string;
  kind: 'regex';
  field: ClassifierTargetField;
  pattern: string;
  negated: boolean;
};

export type CustomizedRuleFilterNode = {
  id: string;
  kind: 'filter';
  filters: GenericFilter[];
  negated: boolean;
};

/** Unsupported criterion kept for round-trip when editing backend-defined rules. */
export type CustomizedRuleOpaqueNode = {
  id: string;
  kind: 'opaque';
  criterion: ClassificationRuleCriterionPayload;
  negated: boolean;
};

export type CustomizedRuleLeafNode =
  CustomizedRuleDescriptionNode | CustomizedRuleRegexNode | CustomizedRuleFilterNode | CustomizedRuleOpaqueNode;

export type CustomizedRuleGroupNode = RuleGroupNode<CustomizedRuleLeafNode>;

export type CustomizedRuleNode = CustomizedRuleGroupNode | CustomizedRuleLeafNode;

let customizedRuleNodeCounter = 0;

export function createCustomizedRuleNodeId(): string {
  customizedRuleNodeCounter += 1;
  return `rule-node-${customizedRuleNodeCounter}`;
}

export function createDefaultDescriptionNode(): CustomizedRuleDescriptionNode {
  return {
    id: createCustomizedRuleNodeId(),
    kind: 'description',
    field: DEFAULT_CLASSIFIER_FIELD,
    description: '',
    negated: false,
  };
}

export function createDefaultRegexNode(): CustomizedRuleRegexNode {
  return {
    id: createCustomizedRuleNodeId(),
    kind: 'regex',
    field: DEFAULT_CLASSIFIER_FIELD,
    pattern: '',
    negated: false,
  };
}

export function createDefaultFilterNode(): CustomizedRuleFilterNode {
  return {
    id: createCustomizedRuleNodeId(),
    kind: 'filter',
    filters: [],
    negated: false,
  };
}

export function createDefaultGroupNode(
  children?: RuleNode<CustomizedRuleLeafNode>[],
  op: RuleGroupOp = 'and',
): CustomizedRuleGroupNode {
  return {
    id: createCustomizedRuleNodeId(),
    kind: 'group',
    op,
    negated: false,
    children: children ?? [createDefaultDescriptionNode()],
  };
}

export function createDefaultCustomizedTree(): CustomizedRuleGroupNode {
  return createDefaultGroupNode();
}

export const CUSTOMIZED_RULE_MAX_CONDITIONS_PER_LEVEL = DEFAULT_RULE_MAX_CONDITIONS_PER_LEVEL;
export const CUSTOMIZED_RULE_MAX_GROUP_NESTING_DEPTH = DEFAULT_RULE_MAX_GROUP_NESTING_DEPTH;

export function canGroupSelectedChildren(
  parent: CustomizedRuleGroupNode,
  parentDepth: number,
  selectedIds: Set<string>,
  maxNestingDepth: number,
): boolean {
  return sharedCanGroupSelectedChildren(parent, parentDepth, selectedIds, maxNestingDepth, createDefaultGroupNode);
}

export function wrapContiguousChildren(
  parent: CustomizedRuleGroupNode,
  parentDepth: number,
  selectedIds: Set<string>,
  maxNestingDepth: number,
): CustomizedRuleGroupNode | null {
  return sharedWrapContiguousChildren(parent, parentDepth, selectedIds, maxNestingDepth, createDefaultGroupNode);
}

export function updateCustomizedRuleGroupInTree(
  node: CustomizedRuleGroupNode,
  groupId: string,
  updater: (group: CustomizedRuleGroupNode) => CustomizedRuleGroupNode,
): CustomizedRuleGroupNode {
  return updateRuleGroupInTree(node, groupId, updater);
}

export function findCustomizedRuleGroupById(
  node: CustomizedRuleGroupNode,
  groupId: string,
): CustomizedRuleGroupNode | null {
  return findRuleGroupById(node, groupId);
}

export function findCustomizedRuleGroupDepth(
  node: CustomizedRuleGroupNode,
  groupId: string,
  currentDepth = 0,
): number | null {
  return findRuleGroupDepth(node, groupId, currentDepth);
}

export function applyGroupNegationToChild(
  group: CustomizedRuleGroupNode,
  child: CustomizedRuleNode,
): CustomizedRuleNode {
  if (!group.negated) return child;

  return { ...child, negated: !child.negated };
}

const classifierFieldLabel = (field: ClassifierTargetField): string => getClassifierFieldOptionLabel(field);

const serializeCustomizedLeaf = (
  node: CustomizedRuleDescriptionNode | CustomizedRuleRegexNode | CustomizedRuleFilterNode | CustomizedRuleOpaqueNode,
): ClassificationRuleCriterionPayload | undefined => {
  if (node.kind === 'opaque') {
    return node.negated ? { type: 'not', criterion: node.criterion } : node.criterion;
  }

  if (node.kind === 'filter') {
    const criterion = classificationRuleCriterionFromFilters(node.filters);
    if (!criterion) return undefined;

    return node.negated ? { type: 'not', criterion } : criterion;
  }

  if (node.kind === 'description') {
    const description = node.description.trim();
    if (!description) return undefined;

    const criterion = llmClassificationRule(node.field, description);
    return node.negated ? { type: 'not', criterion } : criterion;
  }

  const pattern = node.pattern.trim();
  if (!isValidRegexPattern(pattern)) return undefined;

  const criterion = regexMatchRule(node.field, pattern);
  return node.negated ? { type: 'not', criterion } : criterion;
};

const serializeCustomizedNode = (node: CustomizedRuleNode): ClassificationRuleCriterionPayload | undefined => {
  if (node.kind === 'description' || node.kind === 'regex' || node.kind === 'filter' || node.kind === 'opaque') {
    return serializeCustomizedLeaf(node);
  }

  const childCriteria = node.children
    .map(serializeCustomizedNode)
    .filter((criterion): criterion is ClassificationRuleCriterionPayload => criterion !== undefined);

  const criterion = node.op === 'or' ? singleOrAnyOf(childCriteria) : singleOrAllOf(childCriteria);
  if (!criterion) return undefined;

  return node.negated ? { type: 'not', criterion } : criterion;
};

export function tryClassificationRuleFromCustomizedTree(
  tree: CustomizedRuleGroupNode,
): Record<string, unknown> | undefined {
  const rule = serializeCustomizedNode(tree);
  return rule ?? undefined;
}

export function classificationRuleFromCustomizedTree(tree: CustomizedRuleGroupNode): Record<string, unknown> {
  const rule = tryClassificationRuleFromCustomizedTree(tree);
  if (!rule) {
    throw new Error('A customized classification rule requires at least one valid condition.');
  }

  return rule;
}

export type CustomizedLeafValidationError =
  'description_required' | 'pattern_required' | 'pattern_invalid' | 'filter_required';

export const CUSTOMIZED_LEAF_VALIDATION_MESSAGES: Record<CustomizedLeafValidationError, string> = {
  description_required: 'Description is required',
  pattern_required: 'Pattern is required',
  pattern_invalid: 'Invalid regular expression',
  filter_required: 'At least one filter is required',
};

export function getCustomizedLeafValidationError(
  node: CustomizedRuleDescriptionNode | CustomizedRuleRegexNode | CustomizedRuleFilterNode,
): CustomizedLeafValidationError | undefined {
  if (node.kind === 'description') {
    if (!node.description.trim()) return 'description_required';
    return undefined;
  }

  if (node.kind === 'regex') {
    const pattern = node.pattern.trim();
    if (!pattern) return 'pattern_required';
    if (!isValidRegexPattern(pattern)) return 'pattern_invalid';
    return undefined;
  }

  if (node.filters.length === 0 || !node.filters.every(isFilterDefined)) return 'filter_required';

  return undefined;
}

export function getInvalidCustomizedNodeIds(tree: CustomizedRuleGroupNode): Set<string> {
  const invalidIds = new Set<string>();

  const visit = (node: CustomizedRuleNode) => {
    if (node.kind === 'group') {
      if (node.children.length === 0) {
        invalidIds.add(node.id);
        return;
      }

      for (const child of node.children) {
        visit(child);
      }
      return;
    }

    if (node.kind === 'opaque') return;

    if (getCustomizedLeafValidationError(node)) {
      invalidIds.add(node.id);
    }
  };

  visit(tree);
  return invalidIds;
}

export function isCustomizedTreeValid(tree: CustomizedRuleGroupNode): boolean {
  return getInvalidCustomizedNodeIds(tree).size === 0;
}

type PreviewFilter = GenericFilter & { select_all?: boolean };

const formatPreviewFilterValues = (filter: PreviewFilter): string => {
  if (filter.select_all) return 'all';

  const nonEmptyValues = filter.values.filter(Boolean);
  if (nonEmptyValues.length === 0) return '_';
  if (nonEmptyValues.length === 1) return nonEmptyValues[0];

  return `${nonEmptyValues[0]} +${nonEmptyValues.length - 1}`;
};

const previewFilterLeaf = (filter: GenericFilter): string => {
  const column = filter.column.replace(/^tag:/, '');
  const operator = filterToOperator(filter);

  if (operator === 'contains') {
    const search = filter.search?.trim() || '_';
    return `${column} contains "${search}"`;
  }

  const values = formatPreviewFilterValues(filter as PreviewFilter);
  if (operator === 'is_not') {
    return `${column} is not "${values}"`;
  }

  return `${column} eq "${values}"`;
};

const isActiveFilterForPreview = (filter: GenericFilter): boolean => {
  if ((filter as PreviewFilter).select_all) return true;
  if (isContainsFilter(filter)) return !!filter.search?.trim();
  return filter.values.some(Boolean);
};

const previewCustomizedLeaf = (
  node: CustomizedRuleDescriptionNode | CustomizedRuleRegexNode | CustomizedRuleFilterNode | CustomizedRuleOpaqueNode,
): string => {
  if (node.kind === 'opaque') {
    const type = typeof node.criterion.type === 'string' ? node.criterion.type : 'condition';
    if (node.criterion.type === 'llm_classification') {
      const fields = getLlmClassificationFields(node.criterion);
      const description =
        typeof node.criterion.description === 'string' ? node.criterion.description.trim() : '';
      const fieldsPart = fields.length > 0 ? ` on ${fields.join(', ')}` : '';
      if (description) return `text contains "${description}"${fieldsPart}`;
      if (fields.length > 0) return `${type}${fieldsPart}`;
    }
    const field = typeof node.criterion.field === 'string' ? node.criterion.field : undefined;
    return field ? `${type} on ${field}` : type;
  }

  if (node.kind === 'filter') {
    const activeFilters = node.filters.filter(isActiveFilterForPreview);
    if (activeFilters.length === 0) return 'field eq "_"';

    const firstPreview = previewFilterLeaf(activeFilters[0]);
    const remainingCount = activeFilters.length - 1;
    if (remainingCount === 0) return firstPreview;

    return `${firstPreview} and +${remainingCount} filters`;
  }

  const fieldLabel = classifierFieldLabel(node.field).toLowerCase();

  if (node.kind === 'description') {
    const text = node.description.trim() || '_';
    return `text contains "${text}" on ${fieldLabel}`;
  }

  const pattern = node.pattern.trim() || '_';
  return `regex matches /${pattern}/ on ${fieldLabel}`;
};

const previewCustomizedNode = (node: CustomizedRuleNode, asChild = false): string => {
  if (node.kind === 'description' || node.kind === 'regex' || node.kind === 'filter' || node.kind === 'opaque') {
    const preview = previewCustomizedLeaf(node);
    return node.negated ? `not (${preview})` : preview;
  }

  const childPreviews = node.children.map((child) => previewCustomizedNode(child, true)).filter(Boolean);
  if (childPreviews.length === 0) return '_';

  const joiner = node.op === 'or' ? ' or ' : ' and ';
  let joined: string;

  if (childPreviews.length === 1) {
    joined = childPreviews[0];
  } else if (asChild) {
    joined = `(${childPreviews.join(joiner)})`;
  } else {
    joined = childPreviews.join(joiner);
  }

  if (!node.negated) return joined;

  if (childPreviews.length === 1) return `not ${joined}`;

  return `not (${childPreviews.join(joiner)})`;
};

export function customizedTreeExpressionPreview(tree: CustomizedRuleGroupNode): string {
  return formatRuleExpressionPreview(previewCustomizedNode(tree));
}

export type CategoryDefinitionView = 'description' | 'filter' | 'regex' | 'customized';

export type CategoryEditKind = CategoryDefinitionView | 'rename';

const CLASSIFIER_FIELD_VALUES = new Set<ClassifierField>(CLASSIFIER_FIELD_OPTIONS.map((option) => option.value));

export function parseClassifierFieldFromRule(field: unknown): ClassifierTargetField {
  if (field === 'agent_answer') {
    return 'assistant_query';
  }

  if (typeof field === 'string' && CLASSIFIER_FIELD_VALUES.has(field as ClassifierField)) {
    return field as ClassifierField;
  }

  if (typeof field === 'string' && ADJACENT_CLASSIFIER_FIELD_VALUES.has(field as AdjacentClassifierField)) {
    return field as AdjacentClassifierField;
  }

  return DEFAULT_CLASSIFIER_FIELD;
}

export function getCategoryMetadataDescription(category: TaxonomyCategoryResp): string | undefined {
  const metadataDescription = category.category_metadata.description;
  if (typeof metadataDescription === 'string' && metadataDescription !== '' && metadataDescription !== 'None') {
    return metadataDescription;
  }

  return undefined;
}

export function getTaxonomyGroupDescription(aggregate: ProjectAggregate | undefined): string | undefined {
  const description = aggregate?.description;
  if (typeof description === 'string' && description !== '' && description !== 'None') {
    return description;
  }

  return undefined;
}

/**
 * Backend ClassificationRule is `{ criterion, priority? }`. Platform create/update
 * often stores the criterion alone. Normalize to the criterion payload.
 */
export function unwrapClassificationRuleCriterion(
  rule: ClassificationRuleCriterionPayload,
): ClassificationRuleCriterionPayload {
  if (
    typeof rule.type !== 'string' &&
    isClassificationRuleCriterionPayload(rule.criterion) &&
    ('priority' in rule || 'criterion' in rule)
  ) {
    return unwrapClassificationRuleCriterion(rule.criterion);
  }

  return rule;
}

export function unwrapNotForKindCheck(rule: ClassificationRuleCriterionPayload): ClassificationRuleCriterionPayload {
  const unwrapped = unwrapClassificationRuleCriterion(rule);

  if (unwrapped.type === 'not' && isClassificationRuleCriterionPayload(unwrapped.criterion)) {
    return unwrapped.criterion;
  }

  return unwrapped;
}

const criterionToCustomizedNode = (
  rule: ClassificationRuleCriterionPayload,
  negated = false,
): CustomizedRuleNode | undefined => {
  const unwrapped = unwrapClassificationRuleCriterion(rule);

  if (unwrapped.type === 'not') {
    if (!isClassificationRuleCriterionPayload(unwrapped.criterion)) return undefined;

    return criterionToCustomizedNode(unwrapped.criterion, !negated);
  }

  if (unwrapped.type === 'llm_classification') {
    const fields = getLlmClassificationFields(unwrapped);
    if (fields.length === 1) {
      return {
        id: createCustomizedRuleNodeId(),
        kind: 'description',
        field: parseClassifierFieldFromRule(fields[0]),
        description: typeof unwrapped.description === 'string' ? unwrapped.description : '',
        negated,
      };
    }

    return {
      id: createCustomizedRuleNodeId(),
      kind: 'opaque',
      criterion: unwrapped,
      negated,
    };
  }

  if (unwrapped.type === 'regex_match') {
    return {
      id: createCustomizedRuleNodeId(),
      kind: 'regex',
      field: parseClassifierFieldFromRule(unwrapped.field),
      pattern: typeof unwrapped.pattern === 'string' ? unwrapped.pattern : '',
      negated,
    };
  }

  if (unwrapped.type === 'all_of' || unwrapped.type === 'any_of') {
    if (!Array.isArray(unwrapped.criteria)) return undefined;

    if (unwrapped.type === 'all_of') {
      const mergedFilters: ClassificationRuleFilter[] = [];
      let canMergeFilterLeaf = true;

      for (const criterion of unwrapped.criteria) {
        if (!isClassificationRuleCriterionPayload(criterion)) {
          canMergeFilterLeaf = false;
          break;
        }

        const unwrappedChild = unwrapNotForKindCheck(criterion);
        if (
          unwrappedChild.type === 'llm_classification' ||
          unwrappedChild.type === 'regex_match' ||
          unwrappedChild.type === 'all_of' ||
          unwrappedChild.type === 'any_of'
        ) {
          canMergeFilterLeaf = false;
          break;
        }

        const childFilters = filtersFromRuleCriterion(criterion, false);
        if (!childFilters || childFilters.length === 0) {
          canMergeFilterLeaf = false;
          break;
        }

        mergedFilters.push(...childFilters);
      }

      if (canMergeFilterLeaf && mergedFilters.length > 0) {
        return {
          id: createCustomizedRuleNodeId(),
          kind: 'filter',
          filters: normalizeTaxonomyFilters(mergedFilters),
          negated,
        };
      }
    }

    const children: CustomizedRuleNode[] = [];

    for (const criterion of unwrapped.criteria) {
      if (!isClassificationRuleCriterionPayload(criterion)) continue;

      const child = criterionToCustomizedNode(criterion, false);
      if (child) {
        children.push(child);
        continue;
      }

      // Keep unsupported criteria (e.g. field_is_none) for round-trip.
      children.push({
        id: createCustomizedRuleNodeId(),
        kind: 'opaque',
        criterion,
        negated: false,
      });
    }

    if (children.length === 0) return undefined;

    return {
      id: createCustomizedRuleNodeId(),
      kind: 'group',
      op: unwrapped.type === 'any_of' ? 'or' : 'and',
      negated,
      children,
    };
  }

  const filters = filtersFromRuleCriterion(unwrapped, false);
  if (filters && filters.length > 0) {
    return {
      id: createCustomizedRuleNodeId(),
      kind: 'filter',
      filters: normalizeTaxonomyFilters(filters),
      negated,
    };
  }

  if (typeof unwrapped.type === 'string') {
    return {
      id: createCustomizedRuleNodeId(),
      kind: 'opaque',
      criterion: unwrapped,
      negated,
    };
  }

  return undefined;
};

export function customizedTreeFromClassificationRule(
  rule: Record<string, unknown>,
): CustomizedRuleGroupNode | undefined {
  if (!isClassificationRuleCriterionPayload(rule)) return undefined;

  const node = criterionToCustomizedNode(rule, false);
  if (!node) return undefined;

  if (node.kind === 'group') {
    return node;
  }

  return createDefaultGroupNode([node]);
}

const isFilterOnlyGroup = (core: ClassificationRuleCriterionPayload): boolean => {
  if (core.type !== 'all_of' && core.type !== 'any_of') return false;
  if (!Array.isArray(core.criteria) || core.criteria.length === 0) return false;

  return core.criteria.every((criterion) => {
    if (!isClassificationRuleCriterionPayload(criterion)) return false;

    const unwrapped = unwrapNotForKindCheck(criterion);
    if (unwrapped.type === 'llm_classification' || unwrapped.type === 'regex_match') return false;
    if (unwrapped.type === 'all_of' || unwrapped.type === 'any_of') return false;

    return (filtersFromRuleCriterion(unwrapped)?.length ?? 0) > 0;
  });
};

export function getCategoryEditKind(category: TaxonomyCategoryResp): CategoryEditKind {
  const rule = category.category_metadata.classification_rule;
  if (!isClassificationRuleCriterionPayload(rule)) {
    return 'rename';
  }

  const core = unwrapNotForKindCheck(rule);

  if (core.type === 'all_of' || core.type === 'any_of') {
    if (!Array.isArray(core.criteria)) return 'rename';
    if (core.criteria.length === 1) return 'customized';
    if (isFilterOnlyGroup(core)) return 'filter';
    return 'customized';
  }

  if (core.type === 'llm_classification') {
    return getLlmClassificationFields(core).length === 1 ? 'description' : 'customized';
  }

  if (core.type === 'regex_match') {
    return 'regex';
  }

  const criterion = unwrapClassificationRuleCriterion(rule);

  if (filtersFromClassificationRule(criterion).length > 0) {
    return 'filter';
  }

  if (customizedTreeFromClassificationRule(rule)) {
    return 'customized';
  }

  return 'rename';
}

export function retroactiveKeywordFilters(keywords: string[]) {
  if (keywords.length === 0) return undefined;

  return {
    type: 'keyword' as const,
    values: keywords,
  };
}

export const RESERVED_CATEGORY_KEYWORD_ERROR = 'This is a reserved keyword';

export function normalizeTaxonomyClassName(name: string): string {
  return name.trim().toLowerCase();
}

export function normalizeTaxonomyGroupName(name: string): string {
  return snakeCase(name.trim());
}

export function isReservedTaxonomyClassName(name: string, reservedCategoryValues: readonly string[]): boolean {
  const normalized = normalizeTaxonomyClassName(name);
  return reservedCategoryValues.some((value) => normalizeTaxonomyClassName(value) === normalized);
}

export function isReservedTaxonomyGroupName(name: string, reservedCategoryValues: readonly string[]): boolean {
  const normalizedGroup = normalizeTaxonomyGroupName(name);
  return reservedCategoryValues.some((value) => normalizeTaxonomyGroupName(value) === normalizedGroup);
}

export const TAXONOMY_GROUP_DIALOG = {
  add: 'Add Taxonomy Group',
  update: 'Update Taxonomy Group',
} as const;

export const TAXONOMY_GROUP_COST_NOTE = 'Adding new groups will increase computational costs over time.';

/** Steering classifier fields supported by the aggregate API (AI-813). */
export const STEERING_CLASSIFIER_FIELD_OPTIONS = [CLASSIFIER_FIELD_OPTIONS[0], CLASSIFIER_FIELD_OPTIONS[1]] as const;

export type SteeringClassifierField = (typeof STEERING_CLASSIFIER_FIELD_OPTIONS)[number]['value'];

const STEERING_SOURCE_FIELD_VALUES = new Set<SteeringClassifierField>(['query', 'assistant_query']);

export function parseSteeringSourceField(field: unknown): ClassifierTargetField {
  if (field === 'agent_answer') {
    return 'assistant_query';
  }

  if (typeof field === 'string' && STEERING_SOURCE_FIELD_VALUES.has(field as SteeringClassifierField)) {
    return field as SteeringClassifierField;
  }

  if (typeof field === 'string' && ADJACENT_CLASSIFIER_FIELD_VALUES.has(field as AdjacentClassifierField)) {
    return field as AdjacentClassifierField;
  }

  return DEFAULT_CLASSIFIER_FIELD;
}

export function toSteeringSourceFieldApiValue(field: ClassifierTargetField): SteeringClassifierField | null {
  if (field === 'query' || field === 'assistant_query') {
    return field;
  }

  return null;
}

export type GroupRetroactiveMode = 'time_window' | 'last_7_days' | 'all';

export type GroupTimeWindowPreset = '24h' | '7d' | '30d' | '3m' | '6m' | '12m';

export type GroupAdvancedState = {
  classifierField: ClassifierTargetField;
  applyRetroactively: boolean;
  retroactiveMode: GroupRetroactiveMode;
  timeWindowPreset: GroupTimeWindowPreset;
};

export const DEFAULT_GROUP_ADVANCED_STATE: GroupAdvancedState = {
  classifierField: DEFAULT_CLASSIFIER_FIELD,
  applyRetroactively: false,
  retroactiveMode: 'time_window',
  timeWindowPreset: '7d',
};

const PREMIUM_TIME_WINDOW_PRESETS = new Set<GroupTimeWindowPreset>(['6m', '12m']);

const MS_PER_DAY = Duration.from({ days: 1 }).toMillis();

export function isPremiumGroupTimeWindowPreset(preset: GroupTimeWindowPreset): boolean {
  return PREMIUM_TIME_WINDOW_PRESETS.has(preset);
}

export function toTaxonomyEstimateTimeRange(
  state: GroupAdvancedState,
  options: { isSaas: boolean },
): TimeRangeConfig | null {
  const timezone = getTimezoneName();
  const now = Date.now();

  if (options.isSaas) {
    if (state.retroactiveMode !== 'last_7_days') {
      return null;
    }

    const start = subtractDays(now, 7).valueOf();
    return {
      kind: TimeRangeKind['7_days'],
      start: formatToLocaleDateValue(start),
      end: formatToLocaleDateValue(now),
      timezone,
      recompute: true,
    };
  }

  if (state.retroactiveMode !== 'time_window') {
    return null;
  }

  if (isPremiumGroupTimeWindowPreset(state.timeWindowPreset)) {
    return null;
  }

  if (state.timeWindowPreset === '24h') {
    const start = now - MS_PER_DAY;
    return {
      kind: TimeRangeKind.custom,
      start: formatToLocaleDateValue(start),
      end: formatToLocaleDateValue(now),
      timezone,
      recompute: true,
    };
  }

  const kindByPreset = {
    '7d': TimeRangeKind['7_days'],
    '30d': TimeRangeKind['30_days'],
    '3m': TimeRangeKind['3_months'],
  } as const satisfies Record<'7d' | '30d' | '3m', Exclude<TimeRangeKind, 'custom'>>;

  if (state.timeWindowPreset === '7d' || state.timeWindowPreset === '30d' || state.timeWindowPreset === '3m') {
    const resolved = getDateTRangeFromKindWithoutCustom(kindByPreset[state.timeWindowPreset]);
    return {
      ...resolved,
      timezone,
      recompute: true,
    };
  }

  return null;
}

export function toGroupUpsertTimeRange(
  state: GroupAdvancedState,
  options: { isCreate: boolean; isSaas: boolean },
): TimeRangeConfig | undefined {
  if (!options.isCreate || !state.applyRetroactively) {
    return undefined;
  }

  return toTaxonomyEstimateTimeRange(state, { isSaas: options.isSaas }) ?? undefined;
}

export function isGroupRetroactiveSubmitBlocked(
  state: GroupAdvancedState,
  options: { isCreate: boolean; isSaas: boolean },
): boolean {
  if (!options.isCreate || !state.applyRetroactively) {
    return false;
  }

  if (state.retroactiveMode === 'all') {
    return true;
  }

  if (options.isSaas) {
    return false;
  }

  if (state.retroactiveMode === 'last_7_days') {
    return false;
  }

  if (isPremiumGroupTimeWindowPreset(state.timeWindowPreset)) {
    return true;
  }

  return false;
}
